import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_FOREST } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/adventure.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/adventure.mp3',
  attribution: '"Adventure" by JJD [NCS Release] - https://ncs.io/Adventure',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('adventure', variant),
    slug: buildVariantSlug('adventure', variant),
    title: buildVariantTitle('Adventure', variant),
    description:
      'Easy melodic glitch hop pinned half-time at 100 BPM. Steady backbeat with a syncopated lead riff - a friendly step up from On & On.',
    bpm: onsets.bpm,
    difficulty: 2,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_FOREST,
    audio,
  });

  // 60s @ 100 BPM ≈ 100 beats. Verse → chorus drop → quiet bridge → chorus.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 48, kind: 'chorus' });
  builder.section({ startBeat: 48, endBeat: 64, kind: 'bridge' });
  builder.section({ startBeat: 64, endBeat: 100, kind: 'chorus' });

  // Verse: kick + snare backbeat. Loose spacing so the player settles in.
  const versePads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    minSpacing: 1.5,
  });

  // First chorus: kick + snare combo. 1-beat spacing at 100 BPM = 600ms,
  // still comfortable for an "easy" tier track.
  const chorus1 = variantSelectBeats(variant, onsets, {
    fromBeat: 16,
    untilBeat: 48,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    minSpacing: 1,
  });

  // Bridge: melodic lead lull. Leads are detector-bounded at intensity 0.25,
  // so 0.18+ catches the strongest phrase notes - one pad every couple of
  // beats to keep the player engaged through the breakdown.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 48,
    untilBeat: 64,
    kinds: ['lead'],
    minIntensity: 0.18,
    minSpacing: 2,
  });

  // Second chorus: same density as first - matches the song's repeating
  // structure, no surprise difficulty spike.
  const chorus2 = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 96,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    minSpacing: 1,
  });

  const padBeats = mergeBeats(versePads, chorus1, bridgePads, chorus2);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // 100 BPM means a beat is 600ms - minGap 6 = 3.6s of silence before a
  // chamber kicks in, a comfortable cap for an easy track.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 96,
    minGapBeats: 6,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(100);
  return builder.build();
}

export const ADVENTURE_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const ADVENTURE_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(ADVENTURE_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
